import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, UserPlus, Mail, ShieldCheck, QrCode, Trash2,
  RefreshCw, Clock, Users, Settings2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'check_in', label: 'Check-In Staff', desc: 'Only scan tickets at the door.' },
  { value: 'full_manager', label: 'Full Event Manager', desc: 'All permissions, like a co-admin.' },
  { value: 'custom', label: 'Custom Access', desc: 'Pick exactly what they can see.' },
];

export default function AdminTeamManagement() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [features, setFeatures] = useState([]);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [scopeOpts, setScopeOpts] = useState({ events: [], categories: [], tags: [] });
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    email: '',
    name: '',
    role_template: 'check_in',
    permissions: {},
    scope_type: 'all',
    scope_values: { event_ids: [], categories: [], tags: [] },
  });

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, m, i, s] = await Promise.all([
        axios.get(`${API}/team/features`),
        axios.get(`${API}/team/members`, { headers }),
        axios.get(`${API}/team/invites`, { headers }),
        axios.get(`${API}/team/admin/scope-options`, { headers }),
      ]);
      setFeatures(f.data.features);
      setMembers(m.data);
      setInvites(i.data);
      setScopeOpts(s.data);
    } catch (e) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openInvite = () => {
    setForm({
      email: '', name: '', role_template: 'check_in',
      permissions: { check_in: true },
      scope_type: 'all',
      scope_values: { event_ids: [], categories: [], tags: [] },
    });
    setEditing(null);
    setShowInvite(true);
  };

  const openEdit = (member) => {
    setEditing(member);
    setForm({
      email: member.email,
      name: member.name || '',
      role_template: member.role_template || 'custom',
      permissions: member.permissions || {},
      scope_type: member.scope_type || 'all',
      scope_values: member.scope_values || { event_ids: [], categories: [], tags: [] },
    });
    setShowInvite(true);
  };

  const setRole = (role) => {
    let perms = {};
    if (role === 'check_in') perms = { check_in: true };
    if (role === 'full_manager') features.forEach(f => perms[f.key] = true);
    if (role === 'custom') perms = form.permissions;
    setForm(f => ({ ...f, role_template: role, permissions: perms }));
  };

  const togglePerm = (key) => {
    setForm(f => ({ ...f, role_template: 'custom', permissions: { ...f.permissions, [key]: !f.permissions[key] } }));
  };

  const toggleScopeVal = (kind, val) => {
    setForm(f => {
      const cur = new Set(f.scope_values[kind] || []);
      cur.has(val) ? cur.delete(val) : cur.add(val);
      return { ...f, scope_values: { ...f.scope_values, [kind]: Array.from(cur) } };
    });
  };

  const submit = async () => {
    if (!form.email) { toast.error('Email required'); return; }
    if (!Object.values(form.permissions).some(Boolean)) { toast.error('Select at least one permission'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/team/members/${editing.member_id}`, form, { headers });
        toast.success('Team member updated');
      } else {
        await axios.post(`${API}/team/invites`, form, { headers });
        toast.success('Invitation sent');
      }
      setShowInvite(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const revokeMember = async (m) => {
    if (!window.confirm(`Revoke access for ${m.email}?`)) return;
    await axios.delete(`${API}/team/members/${m.member_id}`, { headers });
    toast.success('Access revoked');
    load();
  };

  const revokeInvite = async (i) => {
    if (!window.confirm(`Revoke invite to ${i.email}?`)) return;
    await axios.delete(`${API}/team/invites/${i.invite_id}`, { headers });
    toast.success('Invite revoked');
    load();
  };

  const resendInvite = async (i) => {
    try {
      await axios.post(`${API}/team/invites/${i.invite_id}/resend`, {}, { headers });
      toast.success('Invite email resent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const scopeLabel = (m) => {
    if (m.scope_type === 'all') return 'All events';
    const parts = [];
    const sv = m.scope_values || {};
    if (sv.event_ids?.length) parts.push(`${sv.event_ids.length} event${sv.event_ids.length !== 1 ? 's' : ''}`);
    if (sv.categories?.length) parts.push(`categories: ${sv.categories.join(', ')}`);
    if (sv.tags?.length) parts.push(`tags: ${sv.tags.join(', ')}`);
    return parts.join(' + ') || 'None';
  };

  const roleBadge = (role) => {
    const map = {
      check_in: { label: 'Check-In Staff', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300' },
      full_manager: { label: 'Full Manager', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' },
      custom: { label: 'Custom', cls: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300' },
    };
    const m = map[role] || map.custom;
    return <Badge className={m.cls} variant="outline">{m.label}</Badge>;
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4 sm:px-6 lg:px-8" data-testid="admin-team-page">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate('/admin')} className="flex items-center text-muted-foreground hover:text-foreground mb-6 text-sm" data-testid="team-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
        </button>

        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-2">Team &amp; Access</h1>
            <p className="text-muted-foreground">Invite teammates with scoped permissions — from single-event check-in staff to full co-managers.</p>
          </div>
          <Button onClick={openInvite} className="bg-indigo-600 hover:bg-indigo-700" data-testid="invite-teammate-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Invite Teammate
          </Button>
        </div>

        <Tabs defaultValue="members">
          <TabsList className="mb-6" data-testid="team-tabs">
            <TabsTrigger value="members" data-testid="tab-members"><Users className="w-4 h-4 mr-2" /> Members ({members.filter(m => m.is_active).length})</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites"><Mail className="w-4 h-4 mr-2" /> Pending Invites ({invites.filter(i => i.status === 'pending').length})</TabsTrigger>
          </TabsList>

          <TabsContent value="members">
            {loading ? (
              <div className="text-center text-muted-foreground py-12">Loading…</div>
            ) : members.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No team members yet. Click <strong>Invite Teammate</strong> to add your first.</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4">
                {members.map(m => (
                  <Card key={m.member_id} className={!m.is_active ? 'opacity-60' : ''} data-testid={`member-row-${m.email}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div className="font-semibold">{m.name || m.email}</div>
                            {roleBadge(m.role_template)}
                            {!m.is_active && <Badge variant="destructive">Revoked</Badge>}
                          </div>
                          <div className="text-sm text-muted-foreground">{m.email}</div>
                          <div className="text-xs text-muted-foreground mt-2">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Scope: {scopeLabel(m)}
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {Object.entries(m.permissions || {}).filter(([, v]) => v).map(([k]) => (
                              <span key={k} className="text-xs px-2 py-0.5 rounded bg-muted">
                                {features.find(f => f.key === k)?.label || k}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {m.is_active && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openEdit(m)} data-testid={`edit-member-${m.email}`}>
                                <Settings2 className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => revokeMember(m)} data-testid={`revoke-member-${m.email}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="invites">
            {invites.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">
                <Mail className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>No invites yet.</p>
              </CardContent></Card>
            ) : (
              <div className="grid gap-4">
                {invites.map(i => (
                  <Card key={i.invite_id} data-testid={`invite-row-${i.email}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div className="font-semibold">{i.email}</div>
                            {roleBadge(i.role_template)}
                            <Badge variant={i.status === 'pending' ? 'secondary' : 'outline'}>{i.status}</Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">Sent {new Date(i.created_at).toLocaleString()}</div>
                        </div>
                        {i.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => resendInvite(i)} data-testid={`resend-invite-${i.email}`}>
                              <RefreshCw className="w-4 h-4 mr-1" /> Resend
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => revokeInvite(i)} data-testid={`revoke-invite-${i.email}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Invite / Edit dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.email}` : 'Invite Teammate'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {!editing && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Email</label>
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="teammate@example.com" data-testid="invite-email-input" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Name (optional)</label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="invite-name-input" />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-medium mb-2 block">Role Template</label>
              <div className="grid sm:grid-cols-3 gap-2">
                {ROLE_OPTIONS.map(r => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={`text-left p-3 rounded-lg border transition ${form.role_template === r.value ? 'border-indigo-500 bg-indigo-500/10' : 'border-border hover:border-muted-foreground/40'}`}
                    data-testid={`role-${r.value}`}
                  >
                    <div className="flex items-center gap-2 font-medium text-sm">
                      {r.value === 'check_in' && <QrCode className="w-4 h-4" />}
                      {r.value === 'full_manager' && <ShieldCheck className="w-4 h-4" />}
                      {r.value === 'custom' && <Settings2 className="w-4 h-4" />}
                      {r.label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Permissions</label>
              <div className="grid sm:grid-cols-2 gap-2 p-3 rounded-lg bg-muted/40">
                {features.map(f => (
                  <label key={f.key} className="flex items-center gap-2 cursor-pointer text-sm py-1">
                    <Checkbox checked={!!form.permissions[f.key]} onCheckedChange={() => togglePerm(f.key)} data-testid={`perm-${f.key}`} />
                    {f.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Access Scope</label>
              <Select value={form.scope_type} onValueChange={v => setForm(f => ({ ...f, scope_type: v }))}>
                <SelectTrigger data-testid="scope-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events (full org access)</SelectItem>
                  <SelectItem value="events">Specific events only</SelectItem>
                  <SelectItem value="categories">By category (e.g., all "party" events)</SelectItem>
                  <SelectItem value="tags">By tag (e.g., all "Ladies Night" events)</SelectItem>
                  <SelectItem value="mixed">Mix: events + categories + tags</SelectItem>
                </SelectContent>
              </Select>

              {(form.scope_type === 'events' || form.scope_type === 'mixed') && (
                <div className="mt-3">
                  <div className="text-xs text-muted-foreground mb-1.5">Events ({form.scope_values.event_ids.length} selected)</div>
                  <div className="max-h-40 overflow-y-auto border border-border rounded-lg p-2 space-y-1" data-testid="scope-events-list">
                    {scopeOpts.events.map(e => (
                      <label key={e.event_id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                        <Checkbox checked={form.scope_values.event_ids.includes(e.event_id)} onCheckedChange={() => toggleScopeVal('event_ids', e.event_id)} />
                        <span className="truncate">{e.title} <span className="text-xs text-muted-foreground">— {e.event_date?.slice(0, 10)}</span></span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {(form.scope_type === 'categories' || form.scope_type === 'mixed') && (
                <div className="mt-3">
                  <div className="text-xs text-muted-foreground mb-1.5">Categories</div>
                  <div className="flex flex-wrap gap-2" data-testid="scope-categories-list">
                    {scopeOpts.categories.map(c => (
                      <button key={c} type="button" onClick={() => toggleScopeVal('categories', c)}
                        className={`px-3 py-1 rounded-full text-sm border ${form.scope_values.categories.includes(c) ? 'bg-indigo-600 text-white border-indigo-600' : 'border-border hover:border-muted-foreground/40'}`}
                      >{c}</button>
                    ))}
                    {scopeOpts.categories.length === 0 && <span className="text-xs text-muted-foreground">No categories yet.</span>}
                  </div>
                </div>
              )}

              {(form.scope_type === 'tags' || form.scope_type === 'mixed') && (
                <div className="mt-3">
                  <div className="text-xs text-muted-foreground mb-1.5">Tags</div>
                  <div className="flex flex-wrap gap-2" data-testid="scope-tags-list">
                    {scopeOpts.tags.map(t => (
                      <button key={t} type="button" onClick={() => toggleScopeVal('tags', t)}
                        className={`px-3 py-1 rounded-full text-sm border ${form.scope_values.tags.includes(t) ? 'bg-indigo-600 text-white border-indigo-600' : 'border-border hover:border-muted-foreground/40'}`}
                      >{t}</button>
                    ))}
                    {scopeOpts.tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet.</span>}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)} data-testid="invite-cancel-btn">Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="invite-submit-btn">
              {editing ? 'Save Changes' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
